/**
 * Tableaux de performance de la boutique : mêmes onze colonnes chiffrées,
 * par jour (« Par jour ») ou par pays, site, produit (« Par pays »…), avec
 * une ligne de total figée en bas.
 *
 * Mêmes définitions que les tuiles, ligne par ligne :
 *  · Shipping costs = commandes × coût d'envoi saisi ;
 *  · MC1  = CA HT − Cogs − frais Shopify Payments (% du TTC + fixe par commande, page Frais) ;
 *  · MC1' = MC1 − Shipping costs ; MC1% et MC1'% sur le CA HT ;
 *  · AOV = CA encaissé avant remboursements ÷ commandes ;
 *  · Return Rate = remboursements ÷ CA encaissé.
 * Le total additionne, puis recalcule taux et moyennes sur ces sommes (jamais
 * la moyenne des lignes). Une ligne sans commande n'a ni AOV ni taux (« — »).
 *
 * `visibleRows` : au-delà, le corps défile ; l'en-tête et le total restent
 * figés.
 */

import type { SalesDailyPoint } from '@shared/types/sales';
import { shopPaymentFeeOf, type ShopPaymentFee } from '@shared/types/settings';
import { useLayoutEffect, useRef, useState } from 'react';

import { formatInteger, formatMoney, formatPercent } from '../lib/format';
import { Card } from './Card';

export type ShopFigures = Pick<
  SalesDailyPoint,
  'netRevenue' | 'netRevenueExclTax' | 'cogs' | 'orders' | 'units' | 'grossRevenue' | 'refunds'
> & {
  /**
   * Base de l'AOV quand elle diffère de `orders` : par produit, `orders` compte
   * les commandes au prorata (Shipping costs) et l'AOV divise par les commandes
   * qui contiennent le produit.
   */
  aovOrders?: number;
};

export interface ShopPerformanceRow {
  key: string;
  label: string;
  figures: ShopFigures;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

export const sumShopFigures = (rows: readonly ShopFigures[]): ShopFigures =>
  rows.reduce<ShopFigures>(
    (acc, f) => ({
      netRevenue: acc.netRevenue + f.netRevenue,
      netRevenueExclTax: acc.netRevenueExclTax + f.netRevenueExclTax,
      cogs: acc.cogs + f.cogs,
      orders: acc.orders + f.orders,
      units: acc.units + f.units,
      grossRevenue: acc.grossRevenue + f.grossRevenue,
      refunds: acc.refunds + f.refunds,
    }),
    { netRevenue: 0, netRevenueExclTax: 0, cogs: 0, orders: 0, units: 0, grossRevenue: 0, refunds: 0 },
  );

export function ShopPerformanceTable({
  title,
  subtitle,
  firstHeader,
  rows,
  total,
  visibleRows,
  paymentFee,
  shippingCostPerOrder,
  currency,
}: {
  title: string;
  subtitle: string;
  firstHeader: string;
  rows: readonly ShopPerformanceRow[];
  total: { label: string; figures: ShopFigures };
  visibleRows?: number;
  paymentFee: ShopPaymentFee | null;
  shippingCostPerOrder: number | null;
  currency: string;
}) {
  const money = (value: number | null) => (value === null ? '—' : formatMoney(round2(value), currency));
  const percent = (value: number | null) => (value === null ? '—' : formatPercent(Math.round(value * 10) / 10));

  const cells = (f: ShopFigures, strong = false) => {
    const fees = paymentFee ? shopPaymentFeeOf(paymentFee, f) : null;
    const shipping = shippingCostPerOrder !== null ? f.orders * shippingCostPerOrder : null;
    const mc1 = fees !== null ? f.netRevenueExclTax - f.cogs - fees : null;
    const mc1Prime = mc1 !== null && shipping !== null ? mc1 - shipping : null;
    const share = (value: number | null) => (value !== null && f.netRevenueExclTax > 0 ? (value / f.netRevenueExclTax) * 100 : null);
    const values = [
      money(f.netRevenue),
      money(f.netRevenueExclTax),
      money(f.cogs),
      money(shipping),
      money(mc1Prime),
      percent(share(mc1Prime)),
      money(mc1),
      percent(share(mc1)),
      formatInteger(f.units),
      (f.aovOrders ?? f.orders) > 0 ? money(f.grossRevenue / (f.aovOrders ?? f.orders)) : '—',
      f.grossRevenue > 0 ? percent((f.refunds / f.grossRevenue) * 100) : '—',
    ];
    return values.map((value, index) => (
      <td className="num" key={index}>
        {strong ? <strong>{value}</strong> : value}
      </td>
    ));
  };

  // Hauteur de N lignes + en-tête + total, mesurée plutôt que devinée : elle
  // dépend de la police et du zoom.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const table = wrapRef.current?.querySelector('table');
    if (!table || !visibleRows || rows.length <= visibleRows) {
      setMaxHeight(undefined);
      return;
    }
    const head = table.tHead?.getBoundingClientRect().height ?? 0;
    const foot = table.tFoot?.getBoundingClientRect().height ?? 0;
    const body = [...(table.tBodies[0]?.rows ?? [])].slice(0, visibleRows);
    setMaxHeight(Math.ceil(head + foot + body.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0)));
  }, [rows.length, visibleRows]);

  return (
    <Card title={title} subtitle={subtitle} className="chart-grid__wide">
      {rows.length === 0 ? (
        <p className="empty">Aucune donnée sur cette période.</p>
      ) : (
        <div ref={wrapRef} className="table-wrap table-scroll" style={maxHeight ? { maxHeight } : undefined}>
          <table className="data">
            <thead>
              <tr>
                <th>{firstHeader}</th>
                <th className="num">CA TTC</th>
                <th className="num">CA HT</th>
                <th className="num">Cogs HT</th>
                <th className="num">Envoi HT</th>
                <th className="num">MC1' HT</th>
                <th className="num">MC1'%</th>
                <th className="num">MC1 HT</th>
                <th className="num">MC1%</th>
                <th className="num">Unités</th>
                <th className="num">Panier TTC</th>
                <th className="num">Taux de retour</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>{row.label}</td>
                  {cells(row.figures)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <strong>{total.label}</strong>
                </td>
                {cells(total.figures, true)}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Card>
  );
}
